import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AWS_CONFIG } from '../config/aws';

// 관리자 중고차 구매 문의 목록 (DealerInquiriesManager 미러).
// GET /dealer-inquiries?kind=used-car 로 전용 테이블 조회, 상태 변경 시 kind 동봉.

const STATUS_LABELS = { new: '신규', contacted: '연락함', 'in-progress': '진행중', closed: '완료', rejected: '반려' };
const STATUS_COLORS = { new: '#ff9800', contacted: '#4a9fff', 'in-progress': '#00bcd4', closed: '#4caf50', rejected: '#9e9e9e' };
const FILTER_TABS = [
  { key: 'all', label: '전체' }, { key: 'new', label: '신규' },
  { key: 'contacted', label: '연락함' }, { key: 'in-progress', label: '진행중' }, { key: 'closed', label: '완료' },
];

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default function UsedCarInquiriesManager() {
  const { getIdToken } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updating, setUpdating] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const token = await getIdToken();
      if (!token) {
        setFetchError({ message: '로그인 세션이 만료되었습니다. 다시 로그인해주세요.' });
        setLoading(false);
        return;
      }
      const res = await fetch(`${AWS_CONFIG.apiUrl}/dealer-inquiries?kind=used-car`, {
        headers: { Authorization: token },
      });
      if (!res.ok) {
        let body = '';
        try { body = JSON.stringify(await res.json()); } catch { body = await res.text(); }
        setFetchError({ status: res.status, body });
        setLoading(false);
        return;
      }
      const data = await res.json();
      setInquiries(data.inquiries || []);
    } catch (err) {
      setFetchError({ message: err.message || String(err) });
    }
    setLoading(false);
  }, [getIdToken]);

  useEffect(() => { fetchInquiries(); }, [fetchInquiries]);

  const handleStatusChange = async (inquiryId, newStatus) => {
    setUpdating(inquiryId);
    try {
      const token = await getIdToken();
      await fetch(`${AWS_CONFIG.apiUrl}/dealer-inquiries/${inquiryId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ status: newStatus, kind: 'used-car' }),
      });
      await fetchInquiries();
    } catch {
      alert('상태 변경에 실패했습니다.');
    }
    setUpdating(null);
  };

  const filtered = statusFilter === 'all' ? inquiries : inquiries.filter(i => i.status === statusFilter);
  const tabStyle = (active) => ({
    padding: '8px 16px', borderRadius: 'var(--radius)', border: '1px solid',
    cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
    borderColor: active ? 'var(--primary)' : 'var(--border)',
    background: active ? 'var(--primary)' : 'var(--surface)',
    color: active ? '#fff' : 'var(--text)',
  });

  return (
    <div>
      <h1>중고차 구매 문의 관리</h1>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {FILTER_TABS.map(f => (
          <button key={f.key} style={tabStyle(statusFilter === f.key)} onClick={() => setStatusFilter(f.key)}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>로딩 중...</p>
      ) : fetchError ? (
        <div className="card" style={{ padding: 20, borderColor: '#ff5252', background: 'rgba(255, 82, 82, 0.08)' }}>
          <p style={{ color: '#ff5252', fontWeight: 700, marginBottom: 8 }}>❌ 문의를 불러오지 못했습니다</p>
          {fetchError.status && <p style={{ fontSize: '0.9rem' }}>HTTP: {fetchError.status}</p>}
          {fetchError.body && <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', wordBreak: 'break-all' }}>{fetchError.body}</p>}
          {fetchError.message && <p style={{ fontSize: '0.9rem' }}>{fetchError.message}</p>}
          <button className="btn btn-outline" onClick={fetchInquiries} style={{ marginTop: 12, fontSize: '0.85rem', padding: '6px 14px' }}>
            다시 시도
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>문의가 없습니다.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(i => (
            <div key={i.inquiryId} className="card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>{i.name}</span>
                    <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600, color: '#fff', background: STATUS_COLORS[i.status] || '#999' }}>
                      {STATUS_LABELS[i.status] || i.status}
                    </span>
                  </div>
                  <p style={{ margin: '4px 0', fontSize: '0.95rem' }}>
                    <a href={`tel:${i.phone}`} style={{ color: 'var(--primary)', textDecoration: 'none' }}>{i.phone}</a>
                  </p>
                  {i.carType && <p style={{ margin: '4px 0', fontSize: '0.9rem', color: 'var(--text-muted)' }}>희망 차종: {i.carType}</p>}
                  {i.priceRange && <p style={{ margin: '4px 0', fontSize: '0.9rem', color: 'var(--text-muted)' }}>희망 가격대: {i.priceRange}</p>}
                  <p style={{ margin: '4px 0', fontSize: '0.75rem', color: 'var(--text-dim)' }}>접수: {formatDate(i.createdAt)}</p>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {['contacted', 'in-progress', 'closed', 'rejected'].map(s => (
                    i.status !== s && (
                      <button key={s} disabled={updating === i.inquiryId} onClick={() => handleStatusChange(i.inquiryId, s)}
                        style={{ ...tabStyle(false), fontSize: '0.75rem', padding: '4px 10px', color: STATUS_COLORS[s], borderColor: STATUS_COLORS[s] }}>
                        {updating === i.inquiryId ? '...' : STATUS_LABELS[s]}
                      </button>
                    )
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
